import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";
import { QRCodeSVG } from "qrcode.react";
import { 
  Plus, 
  Search, 
  Filter,
  MoreHorizontal,
  Ticket,
  CheckCircle,
  XCircle,
  Copy,
  Clock,
  DollarSign,
  QrCode,
  Download,
  Eye
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

const Vouchers = () => {
  const [vouchers, setVouchers] = useState([]);
  const [beneficiaries, setBeneficiaries] = useState([]);
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showRedeemDialog, setShowRedeemDialog] = useState(false);
  const [showQRDialog, setShowQRDialog] = useState(false);
  const [selectedVoucher, setSelectedVoucher] = useState(null);
  const [formData, setFormData] = useState({
    beneficiary_id: "",
    amount: 300,
    expiration_days: 90,
    notes: ""
  });
  const [redeemData, setRedeemData] = useState({
    amount: 0,
    partner_id: ""
  });

  useEffect(() => {
    fetchData();
  }, [statusFilter]);

  const fetchData = async () => {
    try {
      const [vouchersRes, beneficiariesRes, partnersRes] = await Promise.all([
        axios.get(`${API}/vouchers${statusFilter ? `?status=${statusFilter}` : ''}`),
        axios.get(`${API}/beneficiaries?status=approved`),
        axios.get(`${API}/partners?status=approved`)
      ]);
      setVouchers(vouchersRes.data.items || []);
      setBeneficiaries(beneficiariesRes.data.items || []);
      setPartners(partnersRes.data.items || []);
    } catch (error) {
      toast.error("Failed to fetch data");
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    // Filter vouchers locally by search term
    if (search) {
      const filtered = vouchers.filter(v => 
        v.code.toLowerCase().includes(search.toLowerCase()) ||
        v.beneficiary_name.toLowerCase().includes(search.toLowerCase())
      );
      setVouchers(filtered);
    } else {
      fetchData();
    }
  };

  const handleCreateVoucher = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/vouchers`, formData);
      toast.success("Voucher created successfully");
      setShowCreateDialog(false);
      setFormData({ beneficiary_id: "", amount: 300, expiration_days: 90, notes: "" });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create voucher");
    }
  };

  const handleRedeem = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/vouchers/${selectedVoucher.id}/redeem`, redeemData);
      toast.success("Voucher redeemed successfully");
      setShowRedeemDialog(false);
      setSelectedVoucher(null);
      setRedeemData({ amount: 0, partner_id: "" });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to redeem voucher");
    }
  };

  const handleCancel = async (voucherId) => {
    if (!window.confirm("Are you sure you want to cancel this voucher?")) return;
    try {
      await axios.put(`${API}/vouchers/${voucherId}/cancel`);
      toast.success("Voucher cancelled");
      fetchData();
    } catch (error) {
      toast.error("Failed to cancel voucher");
    }
  };

  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    toast.success("Voucher code copied!");
  };

  const openRedeemDialog = (voucher) => {
    setSelectedVoucher(voucher);
    setRedeemData({ amount: voucher.remaining_amount, partner_id: "" });
    setShowRedeemDialog(true);
  };

  const openQRDialog = (voucher) => {
    setSelectedVoucher(voucher);
    setShowQRDialog(true);
  };

  const downloadQRCode = () => {
    const svg = document.getElementById("voucher-qr-code");
    if (!svg) return;
    
    const svgData = new XMLSerializer().serializeToString(svg);
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    const img = new Image();
    
    img.onload = () => {
      canvas.width = img.width;
      canvas.height = img.height;
      ctx.fillStyle = "white";
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0);
      const pngFile = canvas.toDataURL("image/png");
      
      const downloadLink = document.createElement("a");
      downloadLink.download = `voucher-${selectedVoucher?.code}.png`;
      downloadLink.href = pngFile;
      downloadLink.click();
    };
    
    img.src = "data:image/svg+xml;base64," + btoa(unescape(encodeURIComponent(svgData)));
    toast.success("QR Code downloaded!");
  };

  // Generate verification URL for QR code
  const getVerificationUrl = (voucher) => {
    const baseUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin;
    return `${baseUrl}/verify/${voucher.code}`;
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0
    }).format(amount || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getStatusBadge = (status) => {
    const styles = {
      active: "bg-green-100 text-green-800 border-green-200",
      pending: "bg-yellow-100 text-yellow-800 border-yellow-200",
      redeemed: "bg-blue-100 text-blue-800 border-blue-200",
      expired: "bg-gray-100 text-gray-800 border-gray-200",
      cancelled: "bg-red-100 text-red-800 border-red-200"
    };
    return (
      <Badge variant="outline" className={styles[status] || styles.pending}>
        {status}
      </Badge>
    );
  };

  return (
    <div className="space-y-6" data-testid="vouchers-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Playfair Display, serif' }}>
            Vouchers
          </h1>
          <p className="text-slate-500 mt-1">Issue and manage clothing vouchers</p>
        </div>
        <Button 
          className="bg-amber-600 hover:bg-amber-700 text-white gap-2"
          onClick={() => setShowCreateDialog(true)}
          data-testid="issue-voucher-btn"
        >
          <Plus className="w-4 h-4" />
          Issue Voucher
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <form onSubmit={handleSearch} className="flex-1 flex gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Search by code or beneficiary..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-9"
                  data-testid="search-input"
                />
              </div>
              <Button type="submit" variant="secondary">Search</Button>
            </form>
            <Select value={statusFilter || "all"} onValueChange={(v) => setStatusFilter(v === "all" ? "" : v)}>
              <SelectTrigger className="w-[180px]" data-testid="status-filter">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue placeholder="All Statuses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="redeemed">Redeemed</SelectItem>
                <SelectItem value="expired">Expired</SelectItem>
                <SelectItem value="cancelled">Cancelled</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Voucher Code</TableHead>
                <TableHead>Beneficiary</TableHead>
                <TableHead>Amount</TableHead>
                <TableHead>Remaining</TableHead>
                <TableHead>Expires</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="w-[80px]">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8">
                    <div className="animate-pulse">Loading...</div>
                  </TableCell>
                </TableRow>
              ) : vouchers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-slate-500">
                    No vouchers found
                  </TableCell>
                </TableRow>
              ) : (
                vouchers.map((voucher) => (
                  <TableRow key={voucher.id} data-testid={`voucher-row-${voucher.id}`}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 bg-amber-100 rounded-lg flex items-center justify-center">
                          <Ticket className="w-4 h-4 text-amber-600" />
                        </div>
                        <div>
                          <div className="font-mono font-medium text-sm">{voucher.code}</div>
                          <button 
                            onClick={() => copyCode(voucher.code)}
                            className="text-xs text-slate-400 hover:text-amber-600 flex items-center gap-1"
                          >
                            <Copy className="w-3 h-3" /> Copy
                          </button>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="font-medium">{voucher.beneficiary_name}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1 text-slate-700">
                        <DollarSign className="w-3 h-3" />
                        {formatCurrency(voucher.amount)}
                      </div>
                    </TableCell>
                    <TableCell>
                      <span className={voucher.remaining_amount < voucher.amount ? "text-amber-600" : ""}>
                        {formatCurrency(voucher.remaining_amount)}
                      </span>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1 text-slate-500 text-sm">
                        <Clock className="w-3 h-3" />
                        {formatDate(voucher.expires_at)}
                      </div>
                    </TableCell>
                    <TableCell>{getStatusBadge(voucher.status)}</TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm" data-testid={`actions-${voucher.id}`}>
                            <MoreHorizontal className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => openQRDialog(voucher)}>
                            <QrCode className="w-4 h-4 mr-2 text-amber-600" /> View QR Code
                          </DropdownMenuItem>
                          {voucher.status === "active" && (
                            <>
                              <DropdownMenuItem onClick={() => openRedeemDialog(voucher)}>
                                <CheckCircle className="w-4 h-4 mr-2 text-green-600" /> Redeem
                              </DropdownMenuItem>
                              <DropdownMenuItem 
                                onClick={() => handleCancel(voucher.id)}
                                className="text-red-600"
                              >
                                <XCircle className="w-4 h-4 mr-2" /> Cancel
                              </DropdownMenuItem>
                            </>
                          )}
                          <DropdownMenuItem onClick={() => copyCode(voucher.code)}>
                            <Copy className="w-4 h-4 mr-2" /> Copy Code
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Create Voucher Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Issue New Voucher</DialogTitle>
            <DialogDescription>
              Create a clothing voucher for an approved beneficiary
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleCreateVoucher} className="space-y-4">
            <div>
              <Label htmlFor="beneficiary">Beneficiary *</Label>
              <Select 
                value={formData.beneficiary_id} 
                onValueChange={(v) => setFormData({...formData, beneficiary_id: v})}
              >
                <SelectTrigger data-testid="select-beneficiary">
                  <SelectValue placeholder="Select beneficiary" />
                </SelectTrigger>
                <SelectContent>
                  {beneficiaries.map((b) => (
                    <SelectItem key={b.id} value={b.id}>
                      {b.first_name} {b.last_name} - {b.city}, {b.state}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {beneficiaries.length === 0 && (
                <p className="text-xs text-amber-600 mt-1">No approved beneficiaries available. Approve a beneficiary first.</p>
              )}
            </div>
            
            <div>
              <Label htmlFor="amount">Voucher Amount ($) *</Label>
              <Input
                id="amount"
                type="number"
                min="50"
                max="1000"
                step="50"
                value={formData.amount}
                onChange={(e) => setFormData({...formData, amount: parseInt(e.target.value)})}
                required
                data-testid="input-amount"
              />
              <p className="text-xs text-slate-500 mt-1">Typical range: $200-$500</p>
            </div>

            <div>
              <Label htmlFor="expiration_days">Valid For (Days) *</Label>
              <Select 
                value={formData.expiration_days.toString()} 
                onValueChange={(v) => setFormData({...formData, expiration_days: parseInt(v)})}
              >
                <SelectTrigger data-testid="select-expiration">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="30">30 Days</SelectItem>
                  <SelectItem value="60">60 Days</SelectItem>
                  <SelectItem value="90">90 Days</SelectItem>
                  <SelectItem value="180">180 Days</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="notes">Notes</Label>
              <Textarea
                id="notes"
                value={formData.notes}
                onChange={(e) => setFormData({...formData, notes: e.target.value})}
                placeholder="Optional notes..."
                data-testid="input-notes"
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowCreateDialog(false)}>
                Cancel
              </Button>
              <Button 
                type="submit" 
                className="bg-amber-600 hover:bg-amber-700" 
                disabled={!formData.beneficiary_id}
                data-testid="submit-voucher-btn"
              >
                Issue Voucher
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Redeem Voucher Dialog */}
      <Dialog open={showRedeemDialog} onOpenChange={setShowRedeemDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Redeem Voucher</DialogTitle>
            <DialogDescription>
              Process voucher redemption at a partner retailer
            </DialogDescription>
          </DialogHeader>
          
          {selectedVoucher && (
            <form onSubmit={handleRedeem} className="space-y-4">
              {/* Voucher Info */}
              <div className="voucher-card p-4 bg-slate-50">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-slate-500">Voucher Code</span>
                  <span className="font-mono font-bold">{selectedVoucher.code}</span>
                </div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-slate-500">Beneficiary</span>
                  <span className="font-medium">{selectedVoucher.beneficiary_name}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-500">Available Balance</span>
                  <span className="font-bold text-green-600">{formatCurrency(selectedVoucher.remaining_amount)}</span>
                </div>
              </div>

              <div>
                <Label htmlFor="redeem_amount">Redemption Amount ($) *</Label>
                <Input
                  id="redeem_amount"
                  type="number"
                  min="1"
                  max={selectedVoucher.remaining_amount}
                  value={redeemData.amount}
                  onChange={(e) => setRedeemData({...redeemData, amount: parseFloat(e.target.value)})}
                  required
                  data-testid="input-redeem-amount"
                />
              </div>

              <div>
                <Label htmlFor="partner">Partner Retailer</Label>
                <Select 
                  value={redeemData.partner_id} 
                  onValueChange={(v) => setRedeemData({...redeemData, partner_id: v})}
                >
                  <SelectTrigger data-testid="select-partner">
                    <SelectValue placeholder="Select partner (optional)" />
                  </SelectTrigger>
                  <SelectContent>
                    {partners.map((p) => (
                      <SelectItem key={p.id} value={p.id}>
                        {p.business_name} - {p.city}, {p.state}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setShowRedeemDialog(false)}>
                  Cancel
                </Button>
                <Button 
                  type="submit" 
                  className="bg-green-600 hover:bg-green-700"
                  data-testid="confirm-redeem-btn"
                >
                  Confirm Redemption
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* QR Code Dialog */}
      <Dialog open={showQRDialog} onOpenChange={setShowQRDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <QrCode className="w-5 h-5 text-amber-600" />
              Voucher QR Code
            </DialogTitle>
            <DialogDescription>
              Partners can scan this code at checkout to verify and redeem the voucher
            </DialogDescription>
          </DialogHeader>
          
          {selectedVoucher && (
            <div className="space-y-6">
              {/* QR Code Display */}
              <div className="flex flex-col items-center p-6 bg-white rounded-xl border-2 border-dashed border-slate-200">
                <QRCodeSVG
                  id="voucher-qr-code"
                  value={getVerificationUrl(selectedVoucher)}
                  size={200}
                  level="H"
                  includeMargin={true}
                  bgColor="#ffffff"
                  fgColor="#0f172a"
                />
                <div className="mt-4 text-center">
                  <div className="font-mono text-lg font-bold text-slate-900">
                    {selectedVoucher.code}
                  </div>
                  <div className="text-sm text-slate-500 mt-1">
                    Scan to verify voucher
                  </div>
                </div>
              </div>

              {/* Voucher Details */}
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div className="bg-slate-50 rounded-lg p-3">
                  <div className="text-slate-500">Beneficiary</div>
                  <div className="font-medium text-slate-900">{selectedVoucher.beneficiary_name}</div>
                </div>
                <div className="bg-slate-50 rounded-lg p-3">
                  <div className="text-slate-500">Status</div>
                  <div>{getStatusBadge(selectedVoucher.status)}</div>
                </div>
                <div className="bg-slate-50 rounded-lg p-3">
                  <div className="text-slate-500">Original Value</div>
                  <div className="font-medium text-slate-900">{formatCurrency(selectedVoucher.amount)}</div>
                </div>
                <div className="bg-green-50 rounded-lg p-3">
                  <div className="text-green-600">Remaining Balance</div>
                  <div className="font-bold text-green-700">{formatCurrency(selectedVoucher.remaining_amount)}</div>
                </div>
              </div>

              {/* Expiration */}
              <div className="flex items-center justify-center gap-2 text-sm text-slate-500">
                <Clock className="w-4 h-4" />
                Expires: {formatDate(selectedVoucher.expires_at)}
              </div>

              <DialogFooter className="flex gap-2">
                <Button 
                  variant="outline" 
                  onClick={() => copyCode(selectedVoucher.code)}
                  className="flex-1"
                >
                  <Copy className="w-4 h-4 mr-2" /> Copy Code
                </Button>
                <Button 
                  onClick={downloadQRCode}
                  className="flex-1 bg-amber-600 hover:bg-amber-700"
                  data-testid="download-qr-btn"
                >
                  <Download className="w-4 h-4 mr-2" /> Download QR
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Vouchers;
